import { scopedTranslation } from '@frontend/utils/I18n'
import type { CurrentCycleType, SessionProgressType } from '@frontend/graphql/queries/callingSession'
import { ImpactHeadline } from './ImpactHeadline'
import { SessionProgressBar } from './SessionProgressBar'
import { SimpleStatCard, SplitStatCard, BreakdownLine } from '@components/molecules/StatCard'
import { CurrentCycleCard } from './CurrentCycleCard'

const t = scopedTranslation('components.impact_dashboard')

type ImpactPanelProps = {
  successfulContactsCount: number
  unsuccessfulContactsCount: number
  totalCallsAttemptedCount: number
  completedSupportersCount: number
  sessionProgress: SessionProgressType
  currentCycle: CurrentCycleType | null
  availableSupportersThisCycleCount: number
  callInProgressAt: string | null
  callingSessionId: string
  isPaused: boolean
  showViewAllButton?: boolean
  onCallingSessionStarted?: () => void
  lastAttemptedSupporter?: {
    id: string
    firstName?: string | null
    lastName?: string | null
  } | null
  startDisabledReason?: string
}

export function ImpactPanel({
  successfulContactsCount,
  unsuccessfulContactsCount,
  totalCallsAttemptedCount,
  completedSupportersCount,
  sessionProgress,
  currentCycle,
  availableSupportersThisCycleCount,
  callInProgressAt,
  callingSessionId,
  isPaused,
  showViewAllButton = true,
  onCallingSessionStarted,
  lastAttemptedSupporter,
  startDisabledReason,
}: ImpactPanelProps) {
  return (
    <div className="tw:bg-primary-50 tw:rounded-3xl tw:p-8 tw:flex tw:flex-col tw:gap-5 tw:self-start">
      <span className="tw:text-sm tw:font-bold tw:uppercase tw:text-gray-blue-500">
        {t('our_impact_flag')}
      </span>

      <ImpactHeadline
        successfulCount={successfulContactsCount}
        totalCallsAttemptedCount={totalCallsAttemptedCount}
      />

      <SessionProgressBar
        completedCount={sessionProgress.completedCount}
        totalSupportersCount={sessionProgress.totalSupportersCount}
        percentage={sessionProgress.percentage}
      />

      <div className="tw:flex tw:flex-wrap tw:gap-3">
        <SimpleStatCard
          value={totalCallsAttemptedCount}
          label={t('stats.calls_attempted')}
        />
        <SplitStatCard value={completedSupportersCount} label={t('stats.completed')}>
          <BreakdownLine icon="check-circle" iconColor="tw:text-green-600" label={`${successfulContactsCount} ${t('stats.successful')}`} />
          <BreakdownLine icon="x-circle" iconColor="tw:text-red-600" label={`${unsuccessfulContactsCount} ${t('stats.unsuccessful')}`} />
        </SplitStatCard>
      </div>

      <CurrentCycleCard
        currentCycle={currentCycle}
        availableSupportersCount={availableSupportersThisCycleCount}
        callInProgressAt={callInProgressAt}
        callingSessionId={callingSessionId}
        isPaused={isPaused}
        showViewAllButton={showViewAllButton}
        onCallingSessionStarted={onCallingSessionStarted}
        lastAttemptedSupporter={lastAttemptedSupporter}
        startDisabledReason={startDisabledReason}
      />
    </div>
  )
}
